"use client";

import { useEffect, useRef, useState } from "react";
import { useMotionMode } from "@/context/MotionContext";
import type { ThemeColors } from "@/components/ui/ThemePicker";
import { THEME_LIGHTNESS } from "@/utils/colors";

interface CustomCursorProps {
  hue?: number; // HSL hue value (legacy support)
  isWhiteTheme?: boolean; // Override with white theme
  isLightMode?: boolean; // Use darker colors for light mode
  onHoverTheme?: (theme: ThemeColors | null) => void; // Callback when hovering themed elements
}

// Convert HSL to RGB for gradient usage
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
  };
}

export function CustomCursor({ hue = 50, isWhiteTheme = false, isLightMode = false, onHoverTheme }: CustomCursorProps) {
  const starRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { canAnimate } = useMotionMode();

  const mousePos = useRef({ x: 0, y: 0 });

  // Track current hover theme to avoid redundant callbacks
  const currentHoverThemeRef = useRef<string | null>(null);

  useEffect(() => {
    // Check for touch device
    if (typeof window !== "undefined" && "ontouchstart" in window) {
      return;
    }

    // Don't hide system cursor if motion is disabled
    if (!canAnimate) {
      return;
    }

    // Hide default cursor
    document.body.style.cursor = "none";
    const style = document.createElement("style");
    style.textContent = `* { cursor: none !important; }`;
    document.head.appendChild(style);

    // Throttle element checks - expensive DOM operations
    let lastElementCheck = 0;
    const elementCheckInterval = 100; // Only check every 100ms
    const isHoveringRef = { current: false };

    const handleMouseMove = (e: MouseEvent) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
      if (!isVisible) setIsVisible(true);
      updateCursor(); // Direct update, no animation loop

      // Throttled element check
      const now = Date.now();
      if (now - lastElementCheck > elementCheckInterval) {
        lastElementCheck = now;
        checkElementUnderCursor();
      }
    };

    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);
    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    // Check element under cursor and update hover/theme state
    const checkElementUnderCursor = () => {
      const target = document.elementFromPoint(mousePos.current.x, mousePos.current.y) as HTMLElement | null;
      if (!target) {
        if (isHoveringRef.current) {
          isHoveringRef.current = false;
          setIsHovering(false);
        }
        // Only call if theme changed
        if (currentHoverThemeRef.current !== null) {
          currentHoverThemeRef.current = null;
          onHoverTheme?.(null);
        }
        return;
      }

      // Check if element or parent has cursor-hide (completely hide cursor)
      if (target.closest("[data-cursor-hide]")) {
        setIsVisible(false);
        if (isHoveringRef.current) {
          isHoveringRef.current = false;
          setIsHovering(false);
        }
        if (currentHoverThemeRef.current !== null) {
          currentHoverThemeRef.current = null;
          onHoverTheme?.(null);
        }
        return;
      } else {
        // Show cursor when not over hide element
        setIsVisible(true);
      }

      // Check if element or parent has cursor-ignore
      if (target.closest("[data-cursor-ignore]")) {
        if (isHoveringRef.current) {
          isHoveringRef.current = false;
          setIsHovering(false);
        }
        // Only call if theme changed
        if (currentHoverThemeRef.current !== null) {
          currentHoverThemeRef.current = null;
          onHoverTheme?.(null);
        }
        return;
      }

      // Single combined selector for interactive elements
      const interactiveEl = target.closest("a, button, [role='button'], [data-magnetic]");
      const isInteractive = !!interactiveEl || window.getComputedStyle(target).cursor === "pointer";

      // Only update state if changed
      if (isInteractive !== isHoveringRef.current) {
        isHoveringRef.current = isInteractive;
        setIsHovering(isInteractive);
      }

      // Only check for brand theme when hovering over an interactive element
      if (isInteractive) {
        const themeEl = target.closest("[data-theme-base]") as HTMLElement | null;
        if (themeEl) {
          const base = themeEl.dataset.themeBase;
          const bright = themeEl.dataset.themeBright;
          const hueValue = parseInt(themeEl.dataset.brandHue || "", 10);
          if (base && bright && !isNaN(hueValue)) {
            // Only call if theme changed (use base as key)
            if (currentHoverThemeRef.current !== base) {
              currentHoverThemeRef.current = base;
              onHoverTheme?.({ base, bright, hue: hueValue });
            }
            return;
          }
        }
      }

      // Only call if theme changed
      if (currentHoverThemeRef.current !== null) {
        currentHoverThemeRef.current = null;
        onHoverTheme?.(null);
      }
    };

    // Re-check on scroll (throttled)
    let scrollTimeout: NodeJS.Timeout | null = null;
    const handleScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        checkElementUnderCursor();
      }, 100);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseenter", handleMouseEnter);
    document.addEventListener("mouseleave", handleMouseLeave);

    // Update cursor position and scale - called on mouse move, no continuous animation
    const updateCursor = () => {
      if (!starRef.current) return;

      // Snap to target scale instantly - no animation
      const targetScale = isClicking ? 1.8 : isHovering ? 2.5 : 1;

      // Compute color - always white/black based on light mode
      const main = isLightMode ? { r: 30, g: 30, b: 30 } : { r: 255, g: 255, b: 255 };

      // Contrast shadow for visibility on any background
      const contrastShadow = isLightMode
        ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
        : 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))';

      // Glow opacity - in light mode use darker glow, in dark mode use bright glow
      const glowOpacity = isLightMode ? 0.6 : 1;

      // Apply styles directly - no RAF loop needed
      starRef.current.style.transform = `translate(${mousePos.current.x}px, ${mousePos.current.y}px) translate(-50%, -50%) scale(${targetScale})`;
      starRef.current.style.background = `rgb(${main.r}, ${main.g}, ${main.b})`;
      starRef.current.style.filter = `${contrastShadow} drop-shadow(0 0 6px rgba(${main.r}, ${main.g}, ${main.b}, ${glowOpacity})) drop-shadow(0 0 12px rgba(${main.r}, ${main.g}, ${main.b}, ${glowOpacity * 0.8})) drop-shadow(0 0 20px rgba(${main.r}, ${main.g}, ${main.b}, ${glowOpacity * 0.4}))`;
    };

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseenter", handleMouseEnter);
      document.removeEventListener("mouseleave", handleMouseLeave);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      document.body.style.cursor = "";
      style.remove();
    };
  }, [isHovering, isClicking, isVisible, canAnimate, isLightMode, onHoverTheme]);

  // Don't render on touch devices or if user prefers reduced motion
  if (typeof window !== "undefined" && "ontouchstart" in window) {
    return null;
  }

  if (!canAnimate) {
    return null;
  }

  // Colors and scale are computed and applied directly on mouse move
  // Initial styles here will be overwritten on first mouse move

  // 4-point star clip-path (same as StarLoader)
  // Point distance controls how sharp the star is (50 = center, 0 = full point)
  const pointDist = 45; // Sharp points extending outward
  const cornerDist = 8; // Tight indent between points
  const starClipPath = `polygon(
    50% ${50 - pointDist}%,
    ${50 + cornerDist}% ${50 - cornerDist}%,
    ${50 + pointDist}% 50%,
    ${50 + cornerDist}% ${50 + cornerDist}%,
    50% ${50 + pointDist}%,
    ${50 - cornerDist}% ${50 + cornerDist}%,
    ${50 - pointDist}% 50%,
    ${50 - cornerDist}% ${50 - cornerDist}%
  )`;

  return (
    <div
      ref={starRef}
      aria-hidden="true"
      className="fixed top-0 left-0 pointer-events-none"
      style={{
        width: 16,
        height: 16,
        clipPath: starClipPath,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity var(--duration-normal)',
        willChange: 'transform',
        zIndex: 2147483647
      }}
    />
  );
}
